import { ContractSuretyType, SuretyQuoteStatus } from "@inrev/common";
import { useQuoteComments } from "@portal/api/comments";
import { AdminBondRequestKeyDocuments } from "@portal/components/features/bond-request-documents";
import { messageDraftKey } from "@portal/components/features/message-draft";
import { CommentsSection } from "@portal/components/layout/CommentsSection";
import { Card, Text } from "@portal/components/primitives";
import { BondRequestAttachmentsTable } from "@portal/components/shared/attachments/BondRequestAttachmentsTable";
import type { FC } from "react";
import { AdminReviewReasonList } from "../../../../shared/admin/AdminReviewReasonList";
import { AdminBondRequestBidResultsCard } from "../../../shared/AdminBondRequestBidResultsCard";
import { AdminBondRequestProjectCard } from "../../../shared/AdminBondRequestProjectCard";
import { AdminFollowerControls } from "../../../shared/AdminFollowerControls";
import { AdminBondRequestProcessingBanner } from "../overview/AdminBondRequestProcessingBanner";
import { AdminBondRequestTermsSummary } from "../overview/AdminBondRequestTermsSummary";
import type { BondRequestTabProps } from "../types";

export const AdminBondRequestOverviewTab: FC<BondRequestTabProps> = ({
	request,
	bondFormQuery,
	accountRate,
	accountDisplayName,
	accountNaicsCodes,
	followersControl,
	bidBond,
	underwriting,
}) => {
	const { comments, commentsLoading, createComment, createCommentLoading } = useQuoteComments(
		request.suretyAccountId,
		request.id,
		true,
	);
	const isBidToFinal = request.contractSuretyType === ContractSuretyType.bid_to_final;
	const hasTerms =
		request.status !== SuretyQuoteStatus.review && request.status !== SuretyQuoteStatus.declined;

	return (
		<div className="flex flex-col space-y-[40px]" data-testid="admin-bond-request-overview-tab">
			<AdminBondRequestProcessingBanner underwritingStatus={underwriting?.status} />
			<AdminReviewReasonList reviewReasons={request.reviewReasons} />
			{hasTerms && (
				<Card elevation="raised" className="rounded-xl" data-testid="admin-bond-request-terms-card">
					<Card.Body className="p-6">
						<AdminBondRequestTermsSummary
							request={request}
							bondForm={bondFormQuery.data}
							bondFormIsFetching={bondFormQuery.isFetching}
							bondFormError={bondFormQuery.error}
							accountRate={accountRate}
						/>
					</Card.Body>
				</Card>
			)}
			<Card elevation="raised" className="rounded-xl" data-testid="admin-bond-request-project-card">
				<Card.Body className="p-6">
					<AdminBondRequestProjectCard request={request} accountNaicsCodes={accountNaicsCodes} />
				</Card.Body>
			</Card>
			{isBidToFinal && (
				<AdminBondRequestBidResultsCard
					request={request}
					bidBond={bidBond}
					underwriting={underwriting}
				/>
			)}
			<Card
				elevation="raised"
				className="rounded-xl"
				data-testid="admin-bond-request-key-documents-card"
			>
				<Card.Body className="p-6">
					<div className="flex flex-col w-full space-y-5">
						<Text type="title" color="primary">
							Key Documents
						</Text>
						<AdminBondRequestKeyDocuments
							accountId={request.suretyAccountId}
							quoteId={request.id}
							contractSuretyType={request.contractSuretyType}
							readOnly={request.status === SuretyQuoteStatus.closed}
						/>
					</div>
				</Card.Body>
			</Card>
			<Card
				elevation="raised"
				className="rounded-xl"
				data-testid="admin-bond-request-messages-card"
			>
				<Card.Body className="p-6">
					<CommentsSection
						entityType="Bond"
						exportEntityType="bondRequest"
						comments={comments}
						commentsLoading={commentsLoading}
						createComment={createComment}
						createCommentLoading={createCommentLoading}
						draftKey={messageDraftKey("quotes", request.id)}
						commentDownloadHeader={`${request.displayTitle} Request For ${request.principal.name}`}
						downloadTrackingId="bond-request-admin-comments-download"
						headerEnd={
							<AdminFollowerControls
								{...followersControl}
								manageFollowersSubtext="Followers will receive email notifications for all comments and changes to the status of this bond request"
								agencyId={request.agencyId}
								trackingPrefix="bond-request-admin-comments-follow"
							/>
						}
					/>
				</Card.Body>
			</Card>
			<BondRequestAttachmentsTable
				accountId={request.suretyAccountId}
				quoteId={request.id}
				bondType={request.contractSuretyType}
				allowDelete
				allowUpload
				downloadAllName={`${accountDisplayName} ${request.displayTitle} Attachments`}
			/>
		</div>
	);
};
